import { StreamClient } from "../streamclient";
import { TelemetryHandler } from "../telemetry/telemetryhandler";
import {
    IsWindowsOS,
    IsChromeOS,
    IsiDevice,
    IsMacOS,
    PlatformDetails,
    IsTizenOS,
    IsChromium,
    IsChromeVersionAtLeast,
    IsWebOS,
    Log,
    IsChromeBrowser,
    IsFirefox,
    IsSafari,
    IsXbox
} from "../dependencies";
import { InputConfigFlags, Zoneless } from "../interfaces";
import { RagnarokSettings } from "../util/settings";
import { InputChannel, InputMediaElement, Measurements } from "./inputinterfaces";
import { BoundaryPair, VideoState } from "../rinterfaces";
import { LatencyIndicator } from "../debug/latencyindicator";
import { InputPacketHandler, MoveEventBuffer } from "./inputpackethandler";
import { GamepadRSDMM } from "./gamepadrsdmm";
import { SupportsPointerEvents, GetCaptureTimestamp } from "../util/utils";
import { IMouseFilter, MouseFilter, NullMouseFilter } from "./mousefilter";
import { DeviceStateObserver, DeviceStateType } from "../devicestateobserver";
import { CursorType } from "../nskinterfaces";

const LOGTAG = "mousehandler";

export interface MouseHandlerCallbacks {
    onEnterPointerLock: () => void;
    onExitPointerLock: (sendEscape: boolean) => void;
    isEscapePressed: () => boolean;
}

interface Cursor {
    /** Base64 encoded representation of the cursor image */
    imageStr: string;
    /** The x coordinate of the hotspot of the image */
    hotspotX: number;
    /** The y coordinate of the hotspot of the image */
    hotspotY: number;
    /** Mime type of the image represented by imageStr */
    mimeTypeStr: string;
    /** If set, contains the in-progress or fully decoded cursor image */
    image?: HTMLImageElement;
    /**
     * If set, contains a string which can be used to set the CSS cursor style on an element to display this cursor.
     * This is reset for non-system cursors whenever the DPR changes
     */
    style?: string;
    /** The scale factor/devicePixelRatio-equivalent at which the server captured the cursor image */
    scale: number;
}

interface CursorState {
    absPositioning: boolean;
    confined: boolean;
    /** X coordinate of the cursor, in display-video space */
    absX: number;
    /** Y coordinate of the cursor, in display-video space */
    absY: number;
    /** X offset to be added to next relative movement, in display physical space */
    remX: number;
    /** Y offset to be added to next relative movement, in display physical space */
    remY: number;
    style: string;
    /** The most recent cursor image that we have started decoding, or undefined if the most recent cursor has been
     * decoded */
    imagePendingDecode?: HTMLImageElement;
    /** The current cursor to display */
    cursor: Cursor;
}

interface CursorCanvasState {
    /** The last seen devicePixelRatio, used to scale mouse coordinates, the cursor image, and more */
    dpiRatio: number;
    /** Graphics context for the cursor canvas. Expected to always be non-null */
    graphicsContext?: CanvasRenderingContext2D;
    /** True if the cursor needs to be re-drawn or re-translated */
    dirty: boolean;
    /** True if the cursor image changed or needs to be re-drawn */
    imageChanged: boolean;
    /** True if the cursor canvas is visible */
    showing: boolean;
}

const zIndexDefault = "200";

interface SystemCursor {
    style: string;
    imageStr: string;
    hotspotX: number;
    hotspotY: number;
}

function createSystemCursor(systemCursor: SystemCursor): Cursor {
    return {
        mimeTypeStr: "image/x-icon",
        scale: 1,
        ...systemCursor
    };
}

const DEFAULT_CURSOR: SystemCursor = {
    style: "default",
    imageStr:
        "AAABAAEAICACAAEAAQAwAQAAFgAAACgAAAAgAAAAQAAAAAEAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA////AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAYAAAAOAAAADAAAABwAAAAYAAAAOAAABDAAAAZwAAAHYAAAB4AAAAf+AAAH/AAAB/gAAAfwAAAH4AAAB8AAAAeAAAAHAAAABgAAAAQAAAAAAAAAAAAAAAAAAAA////////////////////////////////////////////5////8P///+D////h////wf//98P///OD///xh///8Af///AP///wAH//8AD///AB///wA///8Af///AP///wH///8D////B////w////8f////P////3/////////8=",
    hotspotX: 2,
    hotspotY: 1
};

const SYSTEM_CURSORS: SystemCursor[] = [
    { style: "none", imageStr: "", hotspotX: 0, hotspotY: 0 },
    // Default - pointer
    DEFAULT_CURSOR,
    {
        // I-beam
        style: "text",
        imageStr:
            "AAABAAEAICACAAEAAQAwAQAAFgAAACgAAAAgAAAAQAAAAAEAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA////AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAfvwAAAAAAAABAAAAAQAAAAEAAAABAAAAAQAAAAEAAAABAAAAAQAAAAEAAAABAAAAAQAAAAEAAAABAAAAAQAAAAEAAAABAAAAAQAAAAEAAAABAAAAfPwAAAAAAAAAAAAAAAAAA/////////////////////////////////////+BA///Agf///z////8/////P////z////8/////P////z////8/////P////z////8/////P////z////8/////P////z////8/////P////z///+BA///Agf////////////8=",
        hotspotX: 8,
        hotspotY: 13
    },
    {
        // Wait
        style: "wait",
        imageStr:
            "AAABAAEAICACAAEAAQAwAQAAFgAAACgAAAAgAAAAQAAAAAEAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA////AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAD/8AAAzzAAAHDgAABfoAAAb2AAADbAAAAfgAAABgAAAAYAAAAGAAAABgAAAAYAAAAGAAAABgAAAB+AAAA/wAAAaWAAAH/gAABWoAAA//AAAP/wAAAAAAAAAAAAA//////////////////////////////////////////+AAf//gAH//4AB//+AAf//wAP//8AD///gB///8A////gf///8P////D////w////8P////D////gf///wD///4Af//8AD///AA///gAH//4AB//+AAf//gAH///////8=",
        hotspotX: 7,
        hotspotY: 12
    },
    {
        // Crosshair
        style: "crosshair",
        imageStr:
            "AAABAAEAICACAAEAAQAwAQAAFgAAACgAAAAgAAAAQAAAAAEAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA////AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABQAAABjAAABgMAAAQBAAAIAIAACACAABAAQAAAIAAAEABAAAgAgAAIAIAABgMAAAICAAABjAAAAFAAAAAAAAA//////////////////////////////////////////////////////////////////////////////////////4////5T///53P//+97///fff//333//79+//+AAP//v37//999///fff//53P///d3///5T////j////////8=",
        hotspotX: 8,
        hotspotY: 8
    },
    {
        // Wait arrow
        style: "progress",
        imageStr:
            "AAABAAEAICACAAEAAQAwAQAAFgAAACgAAAAgAAAAQAAAAAEAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA////AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAD4AAAAiAAAAPgAAABwAAAYIAAAOCAAADAgAABwcAAAYNgAAOCoABDA+AAZwAAAHYAAAB4AAAAf+AAAH/AAAB/gAAAfwAAAH4AAAB8AAAAeAAAAHAAAABgAAAAQAAAAAAAAAAAAAAAAAAAA///////////////////////+A////gP///4D///+A///5wf//8OP//+Dj///h4///wcH/98OA//ODgP/xh4D/8AeA//AP///wAH//8AD///AB///wA///8Af///AP///wH///8D////B////w////8f////P////3/////////8=",
        hotspotX: 2,
        hotspotY: 1
    },
    {
        // Size up-left down-right (NW-SE)
        style: "nwse-resize",
        imageStr:
            "AAABAAEAICACAAEAAQAwAQAAFgAAACgAAAAgAAAAQAAAAAEAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA////AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAP4AAAB+AAAAPgAAAB4AAABuAAAA5gAAAcIAAEOAAABnAAAAdgAAAHgAAAB8AAAAfgAAAH8AAAAAAAAAAAAAA/////////////////////////////////////////////////////////////////////////////////wA///+AP///wD///+A////gP///wD//34A//88GP//GDz//wB+//8A////Af///wH///8A////AH///wA////////8=",
        hotspotX: 9,
        hotspotY: 8
    },
    {
        // Size up-right down-left (NE-SW)
        style: "nesw-resize",
        imageStr:
            "AAABAAEAICACAAEAAQAwAQAAFgAAACgAAAAgAAAAQAAAAAEAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA////AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAfwAAAH4AAAB8AAAAeAAAAHYAAABnAAAAQ4AAAAHCAAAA5gAAAG4AAAAeAAAAPgAAAH4AAAD+AAAAAAAAAAAAA////////////////////////////////////////////////////////////////////////////////wA///8Af///AP///wH///8B////AP///wB+//8YPP//PBj//34A////AP///4D///+A////AP///gD///wA///////8=",
        hotspotX: 9,
        hotspotY: 9
    },
    {
        // Size left right (E-W)
        style: "ew-resize",
        imageStr:
            "AAABAAEAICACAAEAAQAwAQAAFgAAACgAAAAgAAAAQAAAAAEAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA////AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAACAEAABgBgAA4AcAAeAHgAPv98AH7/fgA+/3wAHgB4AA4AcAAGAGAAAgBAAAAAAAAAAAAAAAAAA//////////////////////////////////////////////////////////////////////////////////////+/3///P8///j/H//w/w//4P8H/8AAA/+AAAH/AAAA/4AAAf/AAAP/4P8H//D/D//4/x///P8///7/f//////8=",
        hotspotX: 13,
        hotspotY: 8
    },
    {
        // Size up down (N-S)
        style: "ns-resize",
        imageStr:
            "AAABAAEAICACAAEAAQAwAQAAFgAAACgAAAAgAAAAQAAAAAEAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA////AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEAAAADgAAAB8AAAA/gAAAf8AAAP/gAAAAAAAADgAAAA4AAAAOAAAADgAAAA4AAAAOAAAADgAAAA4AAAAAAAAA/+AAAH/AAAA/gAAAHwAAAA4AAAAEAAAAAAAAAAAAAA//////////////////////////////////////+/////H////g////wH///4A///8AH//+AA///AAH///g////4P///+D////g////4P///+D////g////4P///AAH//4AD///AB///4A////Af///4P////H////7////////8=",
        hotspotX: 9,
        hotspotY: 12
    },
    {
        // Size all (move)
        style: "move",
        imageStr:
            "AAABAAEAICACAAEAAQAwAQAAFgAAACgAAAAgAAAAQAAAAAEAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA////AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAACAAAABwAAAA+AAAAHAAAABwAAAAcAAAAHAAAABwAAAgACAAf3fwAP93+AB/d/AAIAAgAABwAAAAcAAAAHAAAABwAAAAcAAAAPgAAABwAAAAIAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/////////////////////////////f////j////wf///4D///8Af///wf///8H///vB7//zwef/4AAD/8AAAf+AAAD/wAAB/+AAA//zwef/+8Hv///B////wf///wB///+A////wf///+P////3///////////////////////8=",
        hotspotX: 13,
        hotspotY: 12
    },
    // No - slashed circle, crossbones.  Use default instead
    DEFAULT_CURSOR,
    {
        // Hand
        style: "pointer",
        imageStr:
            "AAABAAEAICACAAEAAQAwAQAAFgAAACgAAAAgAAAAQAAAAAEAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA////AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAH+AAAB/gAAA/4AAAP/AAAH/wAAB/8AAA//gAAP/4AAH/+AADttgABzbYAAY2wAAANsAAADYAAAAwAAAAMAAAAAAAAAAAAAA//////////////////////////////////////////////////////////////////////+Af///AD///wA///4AP//+AB///AAf//wAH//4AA//+AAP//AAD//gAA//wAAP/8IAH//mAH///gD///4H///+H////z////////8=",
        hotspotX: 8,
        hotspotY: 3
    },
    {
        // Help - arrow w/ question mark
        style: "help",
        imageStr:
            "AAABAAEAICACAAEAAQAwAQAAFgAAACgAAAAgAAAAQAAAAAEAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA////AAAAAAAAAAAAAAAAAAAAGAAAAAAAAAAAAAAAAAAAABgAAAAYAAAYHAAAOAYAADADAABwwwAAYMMAAODDABDAZgAZwDwAHYAAAB4AAAAf+AAAH/AAAB/gAAAfwAAAH4AAAB8AAAAeAAAAHAAAABgAAAAQAAAAAAAAAAAAAAAAAAAA/////////////+f////D////5//////////n////w///58P//8PB//+D4P//hzB//wYYf98OGH/ODhh/xh8A/8Afgf/AP8P/wAH//8AD///AB///wA///8Af///AP///wH///8D////B////w////8f////P////3/////////8=",
        hotspotX: 2,
        hotspotY: 1
    }
];
/** The ID of the hidden cursor in the system cursors list */
const SYSTEM_CURSOR_HIDDEN_ID = 0;
/** The ID of the default arrow cursor in the system cursors list */
const SYSTEM_CURSOR_ARROW_ID = 1;

const enum RawUpdateState {
    OFF = 0,
    ON_4MS,
    ON_8MS,
    ON_16MS,
    COUNT
}

const enum PointerLockState {
    UNLOCKED,
    LOCKING,
    LOCKED,
    UNLOCKING
}

export class MouseHandler {
    private streamClient: StreamClient;
    private videoTagElement: InputMediaElement;
    private configFlags: InputConfigFlags;
    private videoState: VideoState;
    private mousedownFunc: any;
    private pointerdownFunc: any;
    private mouseupFunc: any;
    private pointerupFunc: any;
    private mousewheelFunc: any;
    private pointerMoveFunc: any;
    private pointerRawUpdateFunc: any;
    private freeMouseMoveFunc: any;
    private freePointerMoveFunc: any;
    private pointerLockEventFunc: any;
    private pointerLockErrorFunc: any;
    private focusChangeFunc: any;
    private fullscreenChangeFunc: any;
    private cursorType: CursorType;
    private windowedMode: boolean;
    private cursorState: CursorState;
    private cursorCanvasState: CursorCanvasState;
    private cursorCache: Map<number, Cursor> = new Map();
    private systemCursors: Cursor[] = SYSTEM_CURSORS.map(createSystemCursor);
    private cursorCanvas: HTMLCanvasElement;
    private pointerLockState: PointerLockState = PointerLockState.UNLOCKED;
    private consecutivePointerLockFailedAttempts: number = 0;
    private allowPointerLock: boolean = false;
    private pressedButtons: Set<number> = new Set();
    private supportsRawUpdate: boolean = false;
    private supportsPointerEvents: boolean = false;
    private supportsCoalescedEvents: boolean = false;
    private isChromium: boolean;
    private telemetry: TelemetryHandler;
    private unadjustedMovementAllowed: boolean = false;
    private unadjustedMovementActive: boolean = false;
    private pointerLockReturnsPromise: boolean = false;
    private measurements: Measurements;
    private rawUpdateState: RawUpdateState = RawUpdateState.OFF;
    private rawCoalesceInterval: number = 0;
    private mouseFilter: IMouseFilter;
    private documentAddEventListener: any;
    private videoAddEventListener: any;
    private documentRemoveEventListener: any;
    private videoRemoveEventListener: any;
    private rsdmmHandler: GamepadRSDMM;
    private zoomInProgress: boolean = false;
    private packetHandler: InputPacketHandler;
    private moveEventBuffer: MoveEventBuffer;
    private platformDetails: PlatformDetails;
    private nonpassiveOptions: AddEventListenerOptions = { passive: false };
    private scaleMovementByDpr: boolean;
    private shouldDrawCursor: boolean = true;
    private isUserInputEnable: boolean = false;
    /** The name of the CSS image-set function supported on this browser, if any. Can be a prefixed version */
    private supportedImageSetName?: string;
    /** True if we should detect if the user pressed Escape when exiting pointer lock and generate key events */
    private sendEscapeOnUserUnlock: boolean;
    /** If non-zero, the pending pointer lock timer used to generate Escape key events */
    private exitPointerLockTimerId: number = 0;

    constructor(
        private callbacks: MouseHandlerCallbacks,
        streamClient: StreamClient,
        videoElement: InputMediaElement,
        cursorChannel: InputChannel,
        telemetry: TelemetryHandler,
        configFlags: InputConfigFlags,
        platformDetails: PlatformDetails,
        rsdmmHandler: GamepadRSDMM,
        measurements: Measurements,
        moveEventBuffer: MoveEventBuffer,
        packetHandler: InputPacketHandler,
        private deviceObserver: DeviceStateObserver
    ) {
        this.platformDetails = platformDetails;
        this.streamClient = streamClient;
        this.videoTagElement = videoElement;
        this.telemetry = telemetry;
        this.configFlags = configFlags;
        this.rsdmmHandler = rsdmmHandler;
        this.measurements = measurements;
        this.moveEventBuffer = moveEventBuffer;
        this.packetHandler = packetHandler;

        cursorChannel.onmessage = msg => this.onCursorMessage(msg);

        // Using video element or screen dimensions to start here ensures the cursor's initial position
        // is correct. If we initialize these to zero, the cursor will only be ble to start at (0, 0).
        /// TODO: Store our cursor coordinates normalized to 0-65535 so we don't need to change them
        /// when the video dimensions change.
        this.videoState = {
            displayVideoWidth: videoElement.clientWidth || window.screen.width,
            displayVideoHeight: videoElement.clientHeight || window.screen.height,
            scalingFactor: 1,
            leftPadding: 0,
            topPadding: 0,
            videoWidth: 0,
            videoHeight: 0,
            viewportHeight: 0,
            offsetX: 0,
            offsetY: 0,
            zoomFactor: 1
        };

        const zoneless = (window as any).zoneless as Zoneless;
        this.documentAddEventListener =
            zoneless?.documentAddEventListener?.bind(document) ??
            document.addEventListener.bind(document);
        this.documentRemoveEventListener =
            zoneless?.documentRemoveEventListener?.bind(document) ??
            document.removeEventListener.bind(document);
        this.videoAddEventListener =
            zoneless?.videoAddEventListener?.bind(videoElement) ??
            zoneless?.documentAddEventListener?.bind(document) ??
            videoElement.addEventListener.bind(videoElement);
        this.videoRemoveEventListener =
            zoneless?.videoRemoveEventListener?.bind(videoElement) ??
            zoneless?.documentRemoveEventListener?.bind(document) ??
            videoElement.removeEventListener.bind(videoElement);

        this.mousedownFunc = this.mousedown.bind(this);
        this.pointerdownFunc = this.pointerdown.bind(this);
        this.mouseupFunc = this.mouseup.bind(this);
        this.pointerupFunc = this.pointerup.bind(this);
        this.mousewheelFunc = this.mousewheel.bind(this);
        this.pointerMoveFunc = this.pointermove.bind(this);
        this.pointerRawUpdateFunc = this.pointerrawupdate.bind(this);
        this.freeMouseMoveFunc = this.freeMouseMove.bind(this);
        this.freePointerMoveFunc = this.freePointerMove.bind(this);
        this.pointerLockEventFunc = this.pointerLockChange.bind(this);
        this.pointerLockErrorFunc = this.pointerLockError.bind(this);
        this.focusChangeFunc = this.onFocusChange.bind(this);
        this.fullscreenChangeFunc = this.onFullscreenChange.bind(this);

        this.windowedMode = !!configFlags.windowedStreaming;
        this.cursorType =
            getCursorTypeOverride() ??
            configFlags.cursorType ??
            (this.windowedMode ? CursorType.FREE : CursorType.SOFTWARE);
        Log.i("{70cc40b}", "{3277c0e}", this.cursorType);
        this.cursorState = {
            absPositioning: false,
            // TODO: allowUnconfined doesn't make much sense. For SW cursor, we only work with it set to true. For HW
            // cursor, we only work with it set to false. We should probably just remove it
            confined: this.cursorType === CursorType.SOFTWARE && !configFlags.allowUnconfined,
            absX: 0,
            absY: 0,
            remX: 0,
            remY: 0,
            style: "",
            cursor: this.systemCursors[SYSTEM_CURSOR_HIDDEN_ID]
        };
        this.cursorCanvasState = {
            dpiRatio: 1,
            dirty: false,
            imageChanged: false,
            showing: false
        };
        const imageSetNames = ["image-set", "-webkit-image-set"];
        this.supportedImageSetName = imageSetNames.find(imageSet =>
            CSS.supports("cursor", `${imageSet}(url(image.bmp) 2x) 0 0, auto`)
        );
        if (this.cursorType === CursorType.SOFTWARE) {
            this.cursorCanvas = this.getCursorCanvas(this.videoTagElement);
        } else {
            // For hardware cursor scaling, we don't want the canvas to be added to the DOM, so just create one without
            // setting any attributes
            // TODO: Use OffscreenCanvas if available
            this.cursorCanvas = document.createElement("canvas");
        }
        this.updateCursorCanvasPosition();
        this.updateCursorCanvasState();

        this.supportsRawUpdate = "onpointerrawupdate" in this.videoTagElement;
        this.supportsPointerEvents = SupportsPointerEvents();
        // Firefox supports getCoalescedEvents and returns multiple events, but the movementX/Y fields seem to be
        // absolute values rather than relative. So, make sure we use the non-coalesced path
        this.supportsCoalescedEvents =
            this.supportsPointerEvents &&
            "getCoalescedEvents" in PointerEvent.prototype &&
            !IsFirefox(platformDetails);
        Log.i("{70cc40b}", "{f30b8ee}", (this.supportsCoalescedEvents ? "supported" : "not supported"));

        this.isChromium = IsChromium();

        if (this.supportsRawUpdate) {
            const updateOverride = getRawUpdateOverride();
            if (updateOverride !== undefined) {
                this.setRawUpdate(updateOverride);
            } else if (IsChromeOS(platformDetails)) {
                this.setRawUpdate(RawUpdateState.ON_16MS);
            } else {
                this.setRawUpdate(RawUpdateState.ON_4MS);
            }
            const overridden = updateOverride !== undefined;
            Log.i("{70cc40b}", "{20be0c0}", this.rawCoalesceInterval, (overridden ? " (overridden)" : ""));
        }

        this.unadjustedMovementAllowed =
            configFlags.unadjustedMovement ?? this.getDefaultUnadjustedMovement();

        const avoidFilter =
            IsChromeOS(platformDetails) &&
            IsChromeVersionAtLeast(this.platformDetails, 84, 0, 4147, 94) &&
            !IsChromeVersionAtLeast(this.platformDetails, 88, 0, 4324, 139);
        const needsFilter =
            IsWindowsOS(this.platformDetails) ||
            !avoidFilter ||
            !IsChromeVersionAtLeast(this.platformDetails, 84, 0, 4147, 94);

        if (RagnarokSettings.mouseFilter ?? needsFilter) {
            this.mouseFilter = new MouseFilter();
        } else {
            this.mouseFilter = new NullMouseFilter();
        }

        ["pointerlockchange", "mozpointerlockchange"].forEach(eventType =>
            this.documentAddEventListener(eventType, this.pointerLockEventFunc)
        );
        ["pointerlockerror", "mozpointerlockerror"].forEach(eventType =>
            this.documentAddEventListener(eventType, this.pointerLockErrorFunc)
        );
        this.deviceObserver.addStateListener(DeviceStateType.FOCUS, this.focusChangeFunc);
        this.deviceObserver.addStateListener(DeviceStateType.FULLSCREEN, this.fullscreenChangeFunc);

        // There was an issue until Chrome 99 where movementX/movementY didn't take devicePixelRatio into account, so
        // we needed to handle it. We were checking for isChromium to enable the WAR, but this makes version checks
        // hard. So, keep the WAR enabled for TVs (because we know those are old Chromium versions) and older Chrome
        // versions, and disable it for other Chromium-based browsers regardless of versions. Chromium 99 was released a
        // year ago, so this should be okay
        if (IsWebOS(this.platformDetails) || IsTizenOS(this.platformDetails)) {
            this.scaleMovementByDpr = true;
        } else if (
            IsChromeBrowser(platformDetails) &&
            !IsChromeVersionAtLeast(platformDetails, 99, 0, 4844, 44) &&
            !IsMacOS(platformDetails)
        ) {
            this.scaleMovementByDpr = true;
        } else {
            this.scaleMovementByDpr = false;
        }

        // For hardware cursor mode, start with the default cursor shown instead of a hidden cursor. We don't want to
        // start with the hidden cursor because this can lock the pointer when the stream starts.
        if (this.cursorType === CursorType.HARDWARE) {
            this.handleSystemCursor(SYSTEM_CURSOR_ARROW_ID);
        }

        this.sendEscapeOnUserUnlock =
            this.cursorType === CursorType.HARDWARE &&
            (RagnarokSettings.pointerLockEscape ??
                RagnarokSettings.ragnarokConfig.pointerLockEscape ??
                true);
        Log.d("{70cc40b}", "{ec7c4c5}");
    }

    public uninitialize() {
        ["pointerlockerror", "mozpointerlockerror"].forEach(eventType =>
            this.documentRemoveEventListener(eventType, this.pointerLockErrorFunc)
        );
        ["pointerlockchange", "mozpointerlockchange"].forEach(eventType =>
            this.documentRemoveEventListener(eventType, this.pointerLockEventFunc)
        );
        this.deviceObserver.removeStateListener(DeviceStateType.FOCUS, this.focusChangeFunc);
        this.deviceObserver.removeStateListener(
            DeviceStateType.FULLSCREEN,
            this.fullscreenChangeFunc
        );
    }

    public preRender(_timestamp: number) {
        if (this.cursorCanvasState.dirty) {
            this.cursorCanvasState.dirty = false;
            this.drawCursor(
                this.cursorState.absPositioning,
                this.cursorState.absX,
                this.cursorState.absY
            );
        }
    }

    public postRender() {
        // When using raw pointer updates, the event handler will manage sending scheduled inputs.
        if (this.rawUpdateState === RawUpdateState.OFF) {
            this.packetHandler.sendScheduledPackets();
        }
    }

    private pointerdown(evt: PointerEvent) {
        if (shouldIgnorePointerEvent(evt)) {
            return;
        }
        evt.preventDefault();
        this.mousedown(evt);
        if (this.cursorType === CursorType.FREE) {
            this.videoTagElement.setPointerCapture(evt.pointerId);
        }
    }

    private mousedown(evt: MouseEvent) {
        // If latency is enable then we will create dump on mouse event
        this.streamClient.eventTriggerLatencyDump();

        // If we get a mousedown, we are for sure in focus. Set it here in case we receive
        // mousedown before the focus event.
        this.deviceObserver.setWindowFocus(true);
        this.allowPointerLock = true;
        const timeStamp = GetCaptureTimestamp(evt.timeStamp);
        if (this.shouldPointerLock() && !this.isPointerLocked()) {
            this.updatePointerLock();
            // If the cursor is visible, reposition it to where the click happened. Do nothing if it's hidden because
            // any position sent here is likely to cause a jump in any kind of relative-mode camera on the server.
            if (this.cursorState.absPositioning) {
                this.setCursorPosFromOffset(evt.offsetX, evt.offsetY);

                this.scheduleCursorDraw();
                const coordX = this.cursorState.absX;
                const coordY = this.cursorState.absY;
                // Send the position update to the server
                this.packetHandler.sendCursorPos(true, coordX, coordY, timeStamp);
            }
            // Ignore the mousedown if we used it to lock the pointer
            return;
        }

        LatencyIndicator.getInstance().toggleIndicator();
        this.sendMouseDown(evt.button, timeStamp);
    }

    private pointerup(evt: PointerEvent) {
        if (shouldIgnorePointerEvent(evt)) {
            return;
        }
        evt.preventDefault();
        this.mouseup(evt);
        // We don't need to releasePointerCapture() here because it's automatic upon receiving pointerup
    }

    private mouseup(evt: MouseEvent) {
        LatencyIndicator.getInstance().toggleIndicator();
        this.sendMouseUp(evt.button, GetCaptureTimestamp(evt.timeStamp));
    }

    private mousewheel(evt: WheelEvent) {
        // In windowed mode, ignore events that don't target the video element. In pointer lock, this will always be
        // true, but outside of pointer lock this will allow the page to scroll.
        if (this.windowedMode && evt.target !== this.videoTagElement) {
            return;
        }
        this.packetHandler.sendMouseWheel(evt.deltaY, GetCaptureTimestamp(evt.timeStamp));
        evt.preventDefault();
    }

    private freePointerMove(evt: PointerEvent) {
        if (shouldIgnorePointerEvent(evt)) {
            return;
        }
        evt.preventDefault();

        // Chorded button presses will come through pointermove
        if (evt.button !== -1) {
            // The bit in evt.buttons used for mouse buttons 1 and 2 (middle and right click) is swapped
            // All others are a normal shift: 1 << evt.button
            let maskShift: number;
            switch (evt.button) {
                case 1:
                    maskShift = 2;
                    break;
                case 2:
                    maskShift = 1;
                    break;
                default:
                    maskShift = evt.button;
                    break;
            }
            const timeStamp = GetCaptureTimestamp(evt.timeStamp);
            if (evt.buttons & (1 << maskShift)) {
                this.sendMouseDown(evt.button, timeStamp);
            } else {
                this.sendMouseUp(evt.button, timeStamp);
            }
        }
        this.freeMouseMove(evt);
    }

    private freeMouseMove(evt: MouseEvent) {
        this.setCursorPosFromOffset(evt.offsetX, evt.offsetY);
        this.packetHandler.sendCursorPos(
            true,
            this.cursorState.absX,
            this.cursorState.absY,
            GetCaptureTimestamp(evt.timeStamp)
        );
    }

    private handlePointerEvent(evt: PointerEvent | MouseEvent) {
        if (evt instanceof PointerEvent) {
            if (shouldIgnorePointerEvent(evt)) {
                return;
            }
            evt.preventDefault();
        }
        let callbackTime = performance.now();
        if (
            !this.deviceObserver.getWindowFocus() ||
            (this.shouldPointerLock() && !this.isPointerLocked())
        ) {
            // If we don't have the pointer lock that we need, don't move the mouse.
            return;
        }
        if (evt.movementX === 0 && evt.movementY === 0) {
            return;
        }

        // Had a real mouse event, so make sure to show the cursor
        // if there is one.
        this.setShouldDrawCursor(true);

        let eventsPerAggregation = 1;
        let eventCount = 0;
        const absolute = this.cursorState.absPositioning;
        const firstEventIndex = this.moveEventBuffer.moveEventIndex;
        const processMovement = (ev: MouseEvent) => {
            // Use event timestamp directly for local processing
            if (!this.mouseFilter.update(ev.movementX, ev.movementY, ev.timeStamp)) {
                return;
            }

            let coordX = this.mouseFilter.getX();
            let coordY = this.mouseFilter.getY();
            if (absolute) {
                if (this.cursorState.confined) {
                    this.setCursorPosFromMovement(coordX, coordY);
                } else {
                    this.setCursorPosFromOffset(ev.offsetX, ev.offsetY);
                }

                this.cursorCanvasState.dirty = true;
                coordX = this.cursorState.absX;
                coordY = this.cursorState.absY;
            } else {
                // not absolute
                if (this.scaleMovementByDpr) {
                    // Normalize relative co-ordinates on Chromium browsers (incl. Chrome).
                    // Chromium reports movementX/movementY in physical pixel coordinates,
                    // not scaling for DPI.
                    // These should be reported in CSS pixels to use the same coordinate system
                    // as screenX/screenY do.
                    // Other browsers (Firefox, Safari) report in CSS pixels.
                    let dpr = this.cursorCanvasState.dpiRatio;

                    // Apply the previously stored remainder before scaling this event.
                    coordX += this.cursorState.remX;
                    coordY += this.cursorState.remY;

                    // Calculate and store the remainder of the scaling from this event, to apply to the next event.
                    this.cursorState.remX = coordX % dpr;
                    this.cursorState.remY = coordY % dpr;

                    // Scale this event by the DPI to get CSS pixels.
                    coordX /= dpr;
                    coordY /= dpr;
                }
            }
            const aggregate = eventCount % eventsPerAggregation !== 0;
            this.moveEventBuffer.addMoveEvent(
                absolute,
                coordX,
                coordY,
                GetCaptureTimestamp(ev.timeStamp),
                0,
                callbackTime,
                aggregate
            );
            eventCount++;
        };
        if (evt instanceof PointerEvent && this.supportsCoalescedEvents) {
            const coalesced: PointerEvent[] = (evt as any).getCoalescedEvents();
            if (coalesced.length == 0) {
                // No coalesced events in this PointerEvent; treat as a single event.
                processMovement(evt);
            } else {
                const moveBufferSize = this.moveEventBuffer.moveEvents.length;
                // If we receive too many events at once, aggregate it into one event no matter what.
                // No matter how we aggregate it, the server won't space it out correctly.
                const forceOneEvent = coalesced.length > 2 * moveBufferSize;
                // We try to leave room for 4 mouse events in the buffer so we don't overrun it if we
                // get a couple more events after a large group.
                const allowedEvents = forceOneEvent
                    ? 1
                    : Math.max(moveBufferSize - this.moveEventBuffer.moveEventIndex - 4, 1);
                if (coalesced.length > allowedEvents) {
                    eventsPerAggregation = Math.ceil(coalesced.length / allowedEvents);
                    this.measurements.aggregatedCount += coalesced.length;
                }
                for (let ev of coalesced) {
                    processMovement(ev);
                }
            }
        } else {
            processMovement(evt);
        }

        // We only let the first mouse event in a packet be a group because the group header size is significant
        // if applied to every mouse event (19 byte header vs 22-26 byte event), which limits the number of mouse
        // events we can send at once. The group is only used to provide send and callback times for server
        // telemetry, and including it for the first event in each packet fulfills this use case without bloating
        // the packets.
        if (
            this.packetHandler.protocolVersion > 1 &&
            firstEventIndex === 0 &&
            this.moveEventBuffer.moveEventIndex > firstEventIndex
        ) {
            // Mark all the moves we just added as being part of one group.
            this.moveEventBuffer.setGroupSize(
                firstEventIndex,
                this.moveEventBuffer.moveEventIndex - firstEventIndex
            );
        }
    }

    private pointermove(evt: PointerEvent | MouseEvent) {
        this.handlePointerEvent(evt);
    }

    private pointerrawupdate(evt: PointerEvent) {
        // Check if there are scheduled inputs before handlePointerEvent adds any
        const alreadyScheduled = this.packetHandler.hasScheduledPackets();

        this.handlePointerEvent(evt);

        // If there weren't any inputs scheduled before but there are now, we need to send or
        // schedule the current batch.
        if (!alreadyScheduled && this.packetHandler.hasScheduledPackets()) {
            const msSinceSend = performance.now() - this.packetHandler.lastMoveSendTime;

            // Make sure we don't send events too frequently. We do a similar thing on native because
            // mouse events can come as fast as 1000 times a second, which can overwhelm the network.
            if (msSinceSend >= this.rawCoalesceInterval) {
                this.packetHandler.sendScheduledPackets();
            } else {
                this.packetHandler.timeScheduledPackets(this.rawCoalesceInterval - msSinceSend);
            }
        }
    }

    // Sets the cursor position from a CSS offset from the cursor canvas or video element
    public setCursorPosFromOffset(offsetX: number, offsetY: number) {
        // The offset includes letterbox/pillarbox padding, so remove it
        this.setCursorPosFromDisplayVideo(
            offsetX - this.videoState.leftPadding,
            offsetY - this.videoState.topPadding
        );
    }

    // Sets the cursor position from a relative movementX/Y
    private setCursorPosFromMovement(movementX: number, movementY: number) {
        const scale = this.scaleMovementByDpr ? this.cursorCanvasState.dpiRatio : 1.0;
        this.setCursorPosFromDisplayVideo(
            this.cursorState.absX + movementX / scale,
            this.cursorState.absY + movementY / scale
        );
    }

    // Sets the cursor position using normalized uint16 coordinates provided by the server
    private setCursorPosFromServer(normX: number, normY: number) {
        this.setCursorPosFromDisplayVideo(
            (normX * this.videoState.displayVideoWidth) / 65535,
            (normY * this.videoState.displayVideoHeight) / 65535
        );
    }

    // Sets the cursor position using display-video space coordinates
    // They will be clamped to the rectangle that contains the actual displayed video
    private setCursorPosFromDisplayVideo(x: number, y: number) {
        this.cursorState.absX = Math.min(Math.max(x, 0), this.videoState.displayVideoWidth);
        this.cursorState.absY = Math.min(Math.max(y, 0), this.videoState.displayVideoHeight);
        this.scheduleCursorDraw();
    }

    private getCursorCanvas(videoTagElement: InputMediaElement): HTMLCanvasElement {
        let canvasId = "canvasId1";
        let element: HTMLElement | null = document.getElementById(canvasId);
        if (
            element &&
            element instanceof HTMLCanvasElement &&
            element.parentElement === videoTagElement.parentElement
        ) {
            return element;
        } else {
            if (element) {
                // Choose new canvas ID
                do {
                    canvasId = "canvasId" + Math.round(Math.random() * 10000);
                    element = document.getElementById(canvasId);
                } while (element);
            }
            return this.createCursorCanvasElement(videoTagElement, canvasId);
        }
    }

    private createCursorCanvasElement(
        videoTagElement: InputMediaElement,
        canvasId: string
    ): HTMLCanvasElement {
        let cursorCanvas: HTMLCanvasElement = document.createElement("canvas");
        cursorCanvas.id = canvasId;
        cursorCanvas.style.touchAction = "none";
        cursorCanvas.style.pointerEvents = "none";
        // This ensures the browser puts the cursor on its own layer, putting transform on the GPU.
        cursorCanvas.style.willChange = "transform";
        videoTagElement.insertAdjacentElement("afterend", cursorCanvas);

        return cursorCanvas;
    }

    private updateCursorCanvasPosition(): void {
        if (this.cursorType !== CursorType.SOFTWARE) {
            return;
        }
        const zIndex = zIndexDefault;
        const bounding = this.videoTagElement.getBoundingClientRect();
        const parentBounding =
            this.videoTagElement.parentElement?.getBoundingClientRect() ?? new DOMRect(0, 0, 0, 0);
        const left =
            bounding.left -
            parentBounding.left +
            this.videoState.zoomFactor * this.videoState.leftPadding;
        const top =
            bounding.top -
            parentBounding.top +
            this.videoState.zoomFactor * this.videoState.topPadding;

        this.cursorCanvas.style.position = "absolute";
        this.cursorCanvas.style.left = left + "px";
        this.cursorCanvas.style.top = top + "px";
        this.cursorCanvas.style.zIndex = zIndex;
    }

    public updateVideoState(videoState: VideoState) {
        if (this.videoState.displayVideoWidth && this.videoState.displayVideoHeight) {
            const horizontalScaleFactor =
                videoState.displayVideoWidth / this.videoState.displayVideoWidth;
            const verticalScaleFactor =
                videoState.displayVideoHeight / this.videoState.displayVideoHeight;
            // Scale our absolute coordinates to the new coordinate space
            this.cursorState.absX *= horizontalScaleFactor;
            this.cursorState.absY *= verticalScaleFactor;
        }
        this.videoState = videoState;

        this.updateCursorCanvasPosition();
        this.updateCursorCanvasState();
    }

    private updateCursorCanvasState() {
        // Cache the graphics context for efficiency
        const ctx = this.cursorCanvas.getContext("2d");
        if (ctx) {
            this.cursorCanvasState.graphicsContext = ctx;

            const dpr = window.devicePixelRatio;
            // Make sure we re-render the cursor if the DPR changed
            if (this.cursorCanvasState.dpiRatio != dpr) {
                Log.i("{70cc40b}", "{b786ba9}", dpr);

                this.cursorCanvasState.dpiRatio = dpr;
                this.cursorCanvasState.imageChanged = true;

                // Reset the cursor style for all bitmap cursors. This has no effect for SW cursors which don't generate
                // styles, but for HW cursors this ensures we re-draw them at the new DPR
                // TODO: We still need to trigger a re-draw of the current cursor
                for (const cursor of this.cursorCache.values()) {
                    cursor.style = undefined;
                }

                this.scheduleCursorDraw();
            }
        }
    }

    private getCursorScaling(ratio: number): number {
        // Calculate cursor scaling factor - that is the multiplier used to
        // convert the cursor's pixel size to physical pixels.
        // To avoid blurred cursors this should be an integer multiplier
        // of the cursor's size.

        // Don't factor in the stream size because it will cause a mismatch in cursor size between
        // the hardware cursor the user is used to and our software cursor.
        // On Windows, the cursor scale rounds down to 100%, 150%, 200%, 300%, etc. Note there's no 250%.
        // To avoid non-integer scaling, round down to 100%, 200%, etc. except for the range [150, 200).
        // These will be too small at 100%, so use 200% for them instead.
        // On macOS, only 100% or 200% scaling will be visible here, so this algorithm will not change the scaling.

        // Floating point threshold to avoid in-exact numbers messing up these calculations.
        const threshold = 0.001;
        // Special case [150, 200) to be 200% since 100% will be too small.
        if (ratio >= 1.5 - threshold && ratio < 2 - threshold) {
            return 2;
        }
        // Avoid setting the scaling to 0 if dpiRatio < 1
        return Math.max(1, Math.floor(ratio + threshold));
    }

    // Input coordinates must be pre-scaled to account for the session size:
    private drawCursor(absolute: boolean, x: number, y: number) {
        if (this.cursorType !== CursorType.SOFTWARE) {
            return;
        }
        if (this.shouldDrawCursor && absolute) {
            // TODO: We shouldn't have to call getCursorScaling in several places. Store it in the cursor interface
            const cursorScaling = this.getCursorScaling(
                this.cursorCanvasState.dpiRatio / this.cursorState.cursor.scale
            );
            if (this.cursorCanvasState.imageChanged) {
                this.cursorCanvasState.imageChanged = false;
                this.drawCursorToCanvas(cursorScaling);
            }
            this.updateCursorTransform(x, y, cursorScaling);
            this.showCursorCanvas();
        } else {
            this.hideCursorCanvas();
        }
    }

    private setCursor(cursor: Cursor) {
        const updateImage = () => {
            // Now that we're updating the cursor image, don't let a pending decode replace it.
            this.cursorState.imagePendingDecode = undefined;
            this.cursorState.cursor = cursor;
            this.cursorCanvasState.imageChanged = true;

            if (this.cursorType === CursorType.SOFTWARE) {
                // We scale SW cursors when we draw them in drawCursor
                // TODO: Can we draw them here instead?
                this.scheduleCursorDraw();
            } else {
                // This usually only happens once right after decoding a HW bitmap cursor, but can happen again if
                // the DPR changes
                if (!cursor.style) {
                    const dpr = this.cursorCanvasState.dpiRatio;
                    let cursorScaling = 1;
                    let hotspotScale = 1;
                    if (this.shouldScaleHwCursor()) {
                        cursorScaling = this.getCursorScaling(dpr / cursor.scale);
                        hotspotScale = cursorScaling / dpr;
                    }
                    this.drawCursorToCanvas(cursorScaling);
                    cursor.style = this.makeCursorStyle(
                        this.cursorCanvas.toDataURL("image/bmp"),
                        cursor.hotspotX * hotspotScale,
                        cursor.hotspotY * hotspotScale
                    );
                }
                this.cursorState.style = cursor.style;
                this.updateHardwareCursor();
            }
        };

        if (this.cursorType !== CursorType.SOFTWARE && cursor.style) {
            // HW system/hidden cursor or HW bitmap cursor that already have been decoded and scaled
            updateImage();
        } else if (!isVisibleCursor(cursor)) {
            // SW hidden cursor
            this.cursorState.cursor = cursor;
            this.hideCursorCanvas();
        } else if (cursor.image) {
            if (cursor.image.width !== 0 || cursor.image.height !== 0) {
                // SW cursor that has already been decoded or HW bitmap cursor that has been decoded but not scaled
                updateImage();
            } else {
                // We already started decoding this image. When the existing decode finishes,
                // that callback will update the cursor. We don't need to do anything else.
                this.cursorState.imagePendingDecode = cursor.image;
            }
        } else {
            // HW/SW cursor that hasn't been decoded yet
            const image = new Image();
            cursor.image = image;
            if (!image.decode && image.decoding) {
                image.decoding = "sync";
            }
            image.src = this.makeBase64Url(cursor.imageStr, cursor.mimeTypeStr);
            if (image.decode) {
                // Mark this image as pending decode. When decode succeeds or fails, the cursor will
                // only be updated if this image is still the one pending decode.
                // If this changes in the meantime, that means some other decode callback will update
                // the cursor or it's been updated with an already-decoded image.
                this.cursorState.imagePendingDecode = image;
                // Wait until the image is decoded before using it.
                image
                    .decode()
                    .then(() => {
                        if (this.cursorState.imagePendingDecode === image) {
                            updateImage();
                        }
                    })
                    .catch(_encodingError => {
                        if (this.cursorState.imagePendingDecode === image) {
                            this.cursorState.imagePendingDecode = undefined;
                            // Switch to fallback cursor
                            this.setCursor(this.systemCursors[SYSTEM_CURSOR_ARROW_ID]);
                        }
                    });
            } else {
                updateImage();
            }
        }
    }

    /** Draws the current cursor image to the cursor canvas, resizing it if necessary */
    private drawCursorToCanvas(cursorScaling: number) {
        const currentCursor = this.cursorState.cursor;
        if (!currentCursor.image) {
            Log.e("{70cc40b}", "{bc9bdcd}");
            return;
        }
        const dpr = this.cursorCanvasState.dpiRatio;
        const ctx = this.cursorCanvasState.graphicsContext!;
        const cursorPixelWidth = currentCursor.image.width * cursorScaling;
        const cursorPixelHeight = currentCursor.image.height * cursorScaling;
        // We need an integer number of pixels here, so round up to make sure we don't cut off an edge
        const newCanvasWidth = Math.ceil(cursorPixelWidth);
        const newCanvasHeight = Math.ceil(cursorPixelHeight);
        if (
            newCanvasWidth !== this.cursorCanvas.width ||
            newCanvasHeight !== this.cursorCanvas.height
        ) {
            // Style dimensions are in CSS pixels, but the canvas dimensions are in screen pixels.
            // The style dimensions will be scaled by the browser back up to correct number of
            // screen pixels
            this.cursorCanvas.style.width = cursorPixelWidth / dpr + "px";
            this.cursorCanvas.style.height = cursorPixelHeight / dpr + "px";
            this.cursorCanvas.width = newCanvasWidth;
            this.cursorCanvas.height = newCanvasHeight;
            // Changing the canvas dimensions resets the context to its default settings.
            // So, we need to reapply these every time the cursor dimensions change
            ctx.scale(1, 1);
            ctx.imageSmoothingEnabled = false;
        } else {
            ctx.clearRect(0, 0, this.cursorCanvas.width, this.cursorCanvas.height);
        }
        ctx.drawImage(currentCursor.image, 0, 0, cursorPixelWidth, cursorPixelHeight);
    }

    /** Updates the cursor canvas so the cursor's hotspot is over the given x, y coordinate in display-video space */
    private updateCursorTransform(x: number, y: number, cursorScaling: number) {
        const currentCursor = this.cursorState.cursor;
        const dpr = this.cursorCanvasState.dpiRatio;
        const hotspotScale = cursorScaling / dpr;
        const adjustedX = x * this.videoState.zoomFactor - currentCursor.hotspotX * hotspotScale;
        const adjustedY = y * this.videoState.zoomFactor - currentCursor.hotspotY * hotspotScale;

        // These coordinates are in CSS pixels, which will eventually be converted by the browser
        // to screen pixels by multiplying by DPR. Unless a zoom is in progress, make sure our cursor
        // gets rendered on a pixel boundary so it's not blurry. Do not try to round during a zoom
        // to avoid cursor wiggle.
        let scaleX = adjustedX * dpr;
        let scaleY = adjustedY * dpr;
        if (!this.zoomInProgress) {
            scaleX = Math.round(scaleX);
            scaleY = Math.round(scaleY);
        }
        scaleX = scaleX / dpr;
        scaleY = scaleY / dpr;

        this.cursorCanvas.style.transform = "translate(" + scaleX + "px, " + scaleY + "px)";
    }

    /** Shows the cursor canvas if it's hidden */
    private showCursorCanvas() {
        if (!this.cursorCanvasState.showing) {
            this.cursorCanvasState.showing = true;
            this.cursorCanvas.style.visibility = "visible";
        }
    }

    /** Hides the cursor canvas if it's visible */
    private hideCursorCanvas() {
        if (this.cursorCanvasState.showing) {
            this.cursorCanvasState.showing = false;
            this.cursorCanvas.style.visibility = "hidden";
        }
    }

    private onCursorMessage(msg: MessageEvent): void {
        let uint8Array = new Uint8Array(msg.data);
        const SYSTEM_CURSOR = 0;
        const BITMAP_CURSOR = 1;
        const CONFINEMENT = 10;
        switch (uint8Array[0]) {
            case SYSTEM_CURSOR:
            case BITMAP_CURSOR:
                // Set cursor image message
                let cursorId = uint8Array[1];
                let hotX = uint8Array[2];
                let hotY = uint8Array[3];
                let mimeSize = uint8Array[4];
                let offset = 5;
                let read16 = () => {
                    const result = uint8Array[offset] + (uint8Array[offset + 1] << 8);
                    offset += 2;
                    return result;
                };
                let mimeType = "";
                if (mimeSize != 0) {
                    let mimeArray = uint8Array.subarray(offset, offset + mimeSize);
                    offset += mimeSize;
                    mimeType = new TextDecoder("utf-8").decode(mimeArray);
                }

                const payloadSize = read16();
                let payload = "";
                if (payloadSize != 0) {
                    let payloadArray = uint8Array.subarray(offset, offset + payloadSize);
                    offset += payloadSize;
                    payload = new TextDecoder("utf-8").decode(payloadArray);
                }

                let x: number | undefined;
                let y: number | undefined;
                if (offset + 4 <= uint8Array.byteLength) {
                    x = read16();
                    y = read16();
                }
                let scale: number | undefined;
                if (offset + 2 <= uint8Array.byteLength) {
                    scale = read16() / 100;
                }
                if (uint8Array[0] == SYSTEM_CURSOR) {
                    this.handleSystemCursor(cursorId, x, y);
                } else {
                    this.handleBitmapCursor(cursorId, hotX, hotY, mimeType, payload, x, y, scale);
                }
                break;
            case CONFINEMENT:
                // If !allowUnconfined, do not change the cursor confinement. It will always be enabled.
                if (this.configFlags.allowUnconfined) {
                    // Set cursor confinement message
                    let confine = uint8Array[1];

                    this.setCursorConfinement(confine == 1);
                }
                break;
        }
    }

    private handleSystemCursor(id: number, x?: number, y?: number) {
        if (id >= this.systemCursors.length) {
            Log.e("{70cc40b}", "{de8eabe}", id);
            return;
        }
        this.handleCursorCommon(this.systemCursors[id], x, y);
    }

    private handleBitmapCursor(
        id: number,
        hotspotX: number,
        hotspotY: number,
        mimeType: string,
        image: string,
        x?: number,
        y?: number,
        scale?: number
    ) {
        let cursor: Cursor | undefined;
        if (image.length > 0) {
            // Cache cursor
            cursor = {
                imageStr: image,
                hotspotX: hotspotX,
                hotspotY: hotspotY,
                mimeTypeStr: mimeType,
                // Intentionally check for scale to be not undefined AND not zero.
                // If the server doesn't support the cursor scale value, it will not include it or set it to zero
                scale: scale ? scale : 1
            };
            this.cursorCache.set(id, cursor);
        } else {
            cursor = this.cursorCache.get(id);
            if (cursor === undefined) {
                Log.e("{70cc40b}", "{0196577}", id);
                return;
            }
        }

        this.handleCursorCommon(cursor, x, y);
    }

    private handleCursorCommon(cursor: Cursor, x?: number, y?: number) {
        const isVisible = isVisibleCursor(cursor);
        if (!this.cursorState.absPositioning && isVisible && x !== undefined && y !== undefined) {
            // Only accept the new server position if we're transitioning from a hidden to visible cursor.
            this.setCursorPosFromServer(x, y);
        }

        this.setCursorMovementAbsolute(isVisible);
        this.updatePointerLock();

        this.setCursor(cursor);
        this.updateHardwareCursor();
    }

    private makeCursorStyle(url: string, hotspotX: number, hotspotY: number): string {
        const dpr = this.cursorCanvasState.dpiRatio;
        const imageSetName = this.supportedImageSetName;
        if (imageSetName) {
            return `${imageSetName}(url(${url}) ${dpr}x) ${hotspotX} ${hotspotY}, auto`;
        } else {
            // Note: In this path, we haven't scaled the cursor image
            return `url(${url}) ${hotspotX} ${hotspotY}, auto`;
        }
    }

    /**
     * @returns true if we should scale the HW cursors ourselves.
     * @note We want to scale up the HW cursor ourselves to get a crisp image, but this requires image-set support in
     *       the browser. If image-set isn't available, this returns false to let the browser do all the scaling
     */
    private shouldScaleHwCursor(): boolean {
        return this.supportedImageSetName !== undefined;
    }

    private makeBase64Url(image: string, mimeType: string): string {
        return `data:${mimeType};base64,${image}`;
    }

    private updateHardwareCursor() {
        const target = this.videoTagElement;
        const showDefault =
            !this.isUserInputEnabled() ||
            (this.shouldPointerLock() && !this.isPointerLocked()) ||
            this.cursorType == CursorType.FREE;
        if (showDefault) {
            // If we're in a case where we're not accepting user input, show the hardware cursor.
            // This makes sure they can see where their cursor is pointing.
            target.style.cursor = "default";
        } else if (this.cursorType === CursorType.SOFTWARE) {
            target.style.cursor = "none";
        } else {
            target.style.cursor = this.cursorState.style;
        }
    }

    private scheduleCursorDraw() {
        this.cursorCanvasState.dirty = true;
    }

    private setCursorMovementAbsolute(absolute: boolean) {
        this.cursorState.absPositioning = absolute;
        if (!absolute) {
            this.cursorState.remX = 0;
            this.cursorState.remY = 0;
        }
    }

    private setCursorConfinement(confine: boolean) {
        this.cursorState.confined = confine;
        this.updatePointerLock();
    }

    private onFullscreenChange(fullscreen: boolean) {
        // If we're entering fullscreen, we must be in a user interaction so we can request pointer lock.
        // If we left fullscreen, don't try to take the pointer lock again until we get a user interaction.
        this.allowPointerLock = fullscreen;
        this.updatePointerLock();

        this.updateHardwareCursor();
    }

    private pointerLockChange(_: Event) {
        if (document.pointerLockElement instanceof HTMLElement) {
            this.pointerLockState = PointerLockState.LOCKED;
            this.callbacks.onEnterPointerLock();
            return;
        }
        // If we're here, we've just left pointer lock

        // In this paragraph, "enter callback" is the "if" above where we find out pointer lock was entered and "exit
        // callback" is the rest of this function where we find out pointer lock was exited.
        // When we exit pointer lock, we set the pointer lock state to UNLOCKING and expect to receive this exit
        // callback. Before we get this exit callback, we may request pointer lock again which will set the state
        // to LOCKING and eventually trigger an enter callback to set the state to LOCKED. There should be no way
        // for this enter callback to run before the exit callback because of how the browser queues tasks.
        // So, if the pointer lock state is LOCKED here we can be sure that we did not call exitPointerLock and it
        // must be something the browser or user initiated.
        const userInitiated = this.pointerLockState === PointerLockState.LOCKED;
        this.pointerLockState = PointerLockState.UNLOCKED;

        // Ignore any previous pointer lock exits
        this.stopPointerLockTimer();

        const handleUserUnlock = () => {
            Log.i("{70cc40b}", "{d4b3de3}");
            // If the user explicitly leaves pointer lock, we won't be able to automatically
            // go in and out of it without another user interaction
            this.allowPointerLock = false;
            // User left pointer lock, so this will display the hardware cursor
            this.updateHardwareCursor();
        };

        // If we received a normal escape key down, we don't need to generate another one. This will happen if we're in
        // fullscreen with keyboard lock or on Safari, both of which send normal escape key downs.
        const receivedEscape = this.callbacks.isEscapePressed();
        const maybeSendEscape = this.sendEscapeOnUserUnlock && userInitiated && !receivedEscape;
        // Easy path when we don't care about sending the escape key to the server
        if (!maybeSendEscape) {
            if (userInitiated) {
                handleUserUnlock();
            }
            this.callbacks.onExitPointerLock(false);
            return;
        }

        // Add a short delay here to try to detect if the user exited pointer lock with the Escape key. If we receive
        // a focus lost event, assume it wasn't an Escape press. The focus lost event can come before or after this
        // exit pointer lock event. If it was before, pointerLockState wouldn't have been LOCKED and we wouldn't reach
        // here. If it is coming after, it will stop this timer. Therefore, if this timer expires, we assume the user
        // pressed the escape key.
        this.exitPointerLockTimerId = window.setTimeout(() => {
            handleUserUnlock();
            this.finalizeExitPointerLock(true);
        }, 50);
    }

    private finalizeExitPointerLock(sendEscape: boolean) {
        if (this.exitPointerLockTimerId !== 0) {
            this.stopPointerLockTimer();

            this.callbacks.onExitPointerLock(sendEscape);
        }
    }

    private stopPointerLockTimer(): void {
        if (this.exitPointerLockTimerId !== 0) {
            window.clearTimeout(this.exitPointerLockTimerId);
            this.exitPointerLockTimerId = 0;
        }
    }

    private pointerLockError(_: Event) {
        // We use the promise for pointer lock errors when possible
        if (this.pointerLockReturnsPromise) {
            return;
        }
        this.pointerLockState = PointerLockState.UNLOCKED;
        // We don't know whether we're using the Promise-based requestPointerLock until we actually call it.
        // If the first call to the non-Promise rPL happens to request unadjustedMovement, it will fail.
        // Handle that case here by disabling unadjusted movement.
        if (this.unadjustedMovementAllowed) {
            this.unadjustedMovementAllowed = false;
            Log.e("{70cc40b}", "{7599808}");
            this.updatePointerLock();
        } else {
            Log.e("{70cc40b}", "{d633cd1}");
            this.handlePointerLockFailed();
        }
    }

    private handlePointerLockFailed() {
        this.updateHardwareCursor();
    }

    public shouldPointerLock() {
        return (
            this.isUserInputEnabled() &&
            this.cursorType != CursorType.FREE &&
            this.deviceObserver.getWindowFocus() &&
            (!this.cursorState.absPositioning || this.cursorState.confined) &&
            (this.streamClient.getLdatHandler()?.allowPointerLock() ?? true)
        );
    }

    public isPointerLocked() {
        return (
            this.pointerLockState === PointerLockState.LOCKING ||
            this.pointerLockState === PointerLockState.LOCKED
        );
    }

    public updatePointerLock() {
        const shouldLock = this.shouldPointerLock();
        const isLocked = this.isPointerLocked();
        const shouldUnadjusted = this.unadjustedMovementAllowed && !this.cursorState.absPositioning;
        const isUnadjusted = this.unadjustedMovementActive;
        if (
            this.allowPointerLock &&
            shouldLock &&
            (!isLocked || shouldUnadjusted !== isUnadjusted)
        ) {
            const target = this.videoTagElement;
            // On Android, PointerLock is not supported, but target.rPL returns true. If we see rPL
            // failing consecutively for 3 times with UnknownError, we take it as unsupported.
            if (!!target.requestPointerLock && this.consecutivePointerLockFailedAttempts < 3) {
                this.pointerLockState = PointerLockState.LOCKING;
                // Note that for this to be active the flag chrome://flags/#enable-pointer-lock-options
                // needs to be enabled.  Otherwise, it will have no effect.
                const options = {
                    unadjustedMovement: shouldUnadjusted
                };
                const promise = <Promise<void> | undefined>(
                    (target as any).requestPointerLock(options)
                );
                if (promise) {
                    this.pointerLockReturnsPromise = true;
                    promise
                        .then(() => {
                            this.pointerLockState = PointerLockState.LOCKED;
                            this.unadjustedMovementActive = shouldUnadjusted;
                            // Reset the counter
                            this.consecutivePointerLockFailedAttempts = 0;
                        })
                        .catch((ex: DOMException) => {
                            this.pointerLockState = PointerLockState.UNLOCKED;
                            // Although we don't rely on it right now, this Promise is rejected before
                            // pointerlockerror is called
                            if (ex.name === "NotSupportedError" && shouldUnadjusted) {
                                Log.i("{70cc40b}", "{08fd91e}");
                                this.unadjustedMovementAllowed = false;
                                this.updatePointerLock();
                            } else {
                                // Android pointer lock fires PointerLockResult::kUnknownError
                                // https://bugs.chromium.org/p/chromium/issues/detail?id=1235139
                                if (ex.name == "UnknownError") {
                                    this.consecutivePointerLockFailedAttempts++;
                                    Log.w("{70cc40b}", "{9f2ba9f}", this.consecutivePointerLockFailedAttempts);
                                } else {
                                    Log.e("{70cc40b}", "{4cdd98f}", ex.name, ex.message);
                                }
                                this.handlePointerLockFailed();
                            }
                        });
                }
            } else {
                // If pointer lock isn't available, just assume the pointer is locked
                this.pointerLockState = PointerLockState.LOCKED;
            }
        } else if (!shouldLock && isLocked) {
            if (document.exitPointerLock) {
                this.pointerLockState = PointerLockState.UNLOCKING;
                document.exitPointerLock();
            } else {
                this.pointerLockState = PointerLockState.UNLOCKED;
            }
        }
    }

    public enableUserInput(): void {
        if (this.isUserInputEnable) {
            return;
        }
        this.isUserInputEnable = true;
        // Do not add mouse click handlers for iOS. Hardware mouse is not supported
        // and we can't accurately filter out mouse events that the browser converts
        // from touch events.
        /// TODO: Look into using pointer events on iOS and other platforms
        if (!IsiDevice(this.platformDetails)) {
            this.addClickListeners();
        }
        // We always need to listen to wheel events on the document because Chromium has several wheel handling bugs:
        //   1. Some wheel events in pointer lock don't get dispatched to the video handler, only the document handler
        //   2. preventDefault only works reliably in the document wheel handler
        //   3. In Chromium < 85: https://bugs.chromium.org/p/chromium/issues/detail?id=1054332
        // TODO Investigate further and report/fix the first two bugs in Chromium
        this.documentAddEventListener("wheel", this.mousewheelFunc, this.nonpassiveOptions);
        this.addMoveListener();
        if (this.cursorType === CursorType.SOFTWARE) {
            this.cursorCanvas.style.display = "block";
        }
        this.updatePointerLock();
        this.updateHardwareCursor();
    }

    public disableUserInput(): void {
        if (!this.isUserInputEnable) {
            return;
        }
        this.releasePressedButtons();
        this.isUserInputEnable = false;
        if (!IsiDevice(this.platformDetails)) {
            this.removeClickListeners();
        }
        this.documentRemoveEventListener("wheel", this.mousewheelFunc, this.nonpassiveOptions);
        this.removeMoveListener();
        if (this.cursorType === CursorType.SOFTWARE) {
            this.cursorCanvas.style.display = "none";
        }
        this.updatePointerLock();
        this.updateHardwareCursor();
        this.stopPointerLockTimer();
    }

    public isUserInputEnabled(): boolean {
        return this.isUserInputEnable;
    }

    private setRawUpdate(newState: RawUpdateState) {
        if (!this.supportsRawUpdate || this.rawUpdateState === newState) {
            return;
        }
        if (this.isUserInputEnable) {
            this.removeMoveListener();
        }
        this.rawUpdateState = newState;
        if (this.isUserInputEnable) {
            this.addMoveListener();
        }
        switch (newState) {
            case RawUpdateState.ON_4MS:
                this.rawCoalesceInterval = 4;
                break;
            case RawUpdateState.ON_8MS:
                this.rawCoalesceInterval = 8;
                break;
            case RawUpdateState.ON_16MS:
                this.rawCoalesceInterval = 16;
                break;
            default:
                this.rawCoalesceInterval = 0;
                break;
        }
    }

    private addMoveListener() {
        if (this.cursorType == CursorType.FREE) {
            this.rsdmmHandler.setMoveType(this.getMoveEventName());
            this.videoAddEventListener(this.getMoveEventName(), this.getFreeMoveFunc());
        } else if (this.rawUpdateState !== RawUpdateState.OFF) {
            this.rsdmmHandler.setMoveType("pointerrawupdate");
            this.videoAddEventListener("pointerrawupdate", this.pointerRawUpdateFunc);
        } else {
            this.rsdmmHandler.setMoveType(this.getMoveEventName());
            this.videoAddEventListener(this.getMoveEventName(), this.pointerMoveFunc);
        }
    }

    private removeMoveListener() {
        if (this.cursorType == CursorType.FREE) {
            this.videoRemoveEventListener(this.getMoveEventName(), this.getFreeMoveFunc());
        } else if (this.rawUpdateState !== RawUpdateState.OFF) {
            this.videoRemoveEventListener("pointerrawupdate", this.pointerRawUpdateFunc);
        } else {
            this.videoRemoveEventListener(this.getMoveEventName(), this.pointerMoveFunc);
        }
    }

    private static contextMenuHandler(event: MouseEvent) {
        event.preventDefault();
    }

    private addClickListeners() {
        if (this.cursorType == CursorType.FREE && this.supportsPointerEvents) {
            this.rsdmmHandler.setDownUpTypes("pointerdown", "pointerup");
            this.videoAddEventListener("pointerdown", this.pointerdownFunc);
            this.videoAddEventListener("pointerup", this.pointerupFunc);
        } else {
            this.rsdmmHandler.setDownUpTypes("mousedown", "mouseup");
            this.videoAddEventListener("mousedown", this.mousedownFunc);
            this.videoAddEventListener("mouseup", this.mouseupFunc);
        }

        // We can't disable the context menu on Xbox because it's used to toggle between the Edge-level real gamepad
        // and gamepad-as-mouse modes
        if (!IsXbox(this.platformDetails)) {
            this.videoAddEventListener("contextmenu", MouseHandler.contextMenuHandler);
        }
    }

    private removeClickListeners() {
        if (this.cursorType == CursorType.FREE && this.supportsPointerEvents) {
            this.videoRemoveEventListener("pointerdown", this.pointerdownFunc);
            this.videoRemoveEventListener("pointerup", this.pointerupFunc);
        } else {
            this.videoRemoveEventListener("mousedown", this.mousedownFunc);
            this.videoRemoveEventListener("mouseup", this.mouseupFunc);
        }

        if (!IsXbox(this.platformDetails)) {
            this.videoRemoveEventListener("contextmenu", MouseHandler.contextMenuHandler);
        }
    }

    private onFocusChange(focus: boolean): void {
        // If we receive focus while in fullscreen, the user probably alt tabbed back to us.
        if (focus) {
            if (this.deviceObserver.getFullscreen()) {
                this.allowPointerLock = true;
                this.updatePointerLock();
            }
            this.updateCursorCanvasState();
        } else {
            // If we lost focus, we are probably going to exit pointer lock or already did. Make sure we update here
            // (which will explicitly exit it) and then finalize any pending pointer lock exits to make sure we don't
            // generate an Escape key press.
            this.updatePointerLock();
            this.finalizeExitPointerLock(false);
        }
    }

    public releasePressedButtons() {
        if (this.pressedButtons.size > 0) {
            Log.i("{70cc40b}", "{041c67f}", this.pressedButtons.size);
            for (const button of this.pressedButtons) {
                // deletes button from cache
                this.sendMouseUp(button, 0);
            }
        }
    }

    public getMoveEventName() {
        if (this.supportsPointerEvents) {
            return "pointermove";
        } else {
            return "mousemove";
        }
    }

    private getFreeMoveFunc(): Function {
        if (this.supportsPointerEvents) {
            return this.freePointerMoveFunc;
        } else {
            return this.freeMouseMoveFunc;
        }
    }

    public sendMouseDown(button: number, ts: number) {
        if (this.pressedButtons.has(button)) {
            return;
        }
        this.pressedButtons.add(button);
        this.packetHandler.sendMouseDown(button, ts);
    }

    public sendMouseUp(button: number, ts: number) {
        if (!this.pressedButtons.has(button)) {
            return;
        }
        this.pressedButtons.delete(button);
        this.packetHandler.sendMouseUp(button, ts);
    }

    public setShouldDrawCursor(draw: boolean): void {
        if (this.shouldDrawCursor != draw) {
            this.scheduleCursorDraw();
            this.shouldDrawCursor = draw;
        }
    }

    public setZoomInProgress(zoom: boolean): void {
        this.zoomInProgress = zoom;
        this.scheduleCursorDraw();
    }

    public getCursorState(): CursorState {
        return this.cursorState;
    }

    public toggleUnadjustedMovement(): void {
        this.unadjustedMovementAllowed = !this.unadjustedMovementAllowed;
        const message = "UnadjustedMovement: " + (this.unadjustedMovementAllowed ? "ON" : "OFF");
        this.streamClient.showDebugMessage(message);
        Log.i("{70cc40b}", "{796c5cb}", (this.unadjustedMovementAllowed ? "ON" : "OFF"));
    }

    public setNextRawUpdate(): void {
        let status = "UNSUPPORTED";
        if (this.supportsRawUpdate) {
            this.setRawUpdate((this.rawUpdateState + 1) % RawUpdateState.COUNT);
            switch (this.rawUpdateState) {
                case RawUpdateState.ON_4MS:
                    status = "4ms";
                    break;
                case RawUpdateState.ON_8MS:
                    status = "8ms";
                    break;
                case RawUpdateState.ON_16MS:
                    status = "16ms";
                    break;
                default:
                    status = "OFF";
                    break;
            }
        }
        const message = "Non-vsync mouse events: " + status;
        this.streamClient.showDebugMessage(message);
        Log.i("{70cc40b}", "{bac28ff}", status);
    }

    private getDefaultUnadjustedMovement(): boolean {
        // Dynamically enable requestPointerLock unadjustedMovement if we're:
        // - On Windows and Chrome 84+ and not windowed mode: Chrome 83 had a bug that broke pointer lock
        // - On ChromeOS/macOS and Chrome 86+: First version to be fully implemented on ChromeOS/macOS
        if (IsWindowsOS(this.platformDetails)) {
            // Disable unadjusted movement if we're windowed. There's a Chrome bug that lets the mouse interact with
            // the scroll bar, permissions dialogs, etc. when unadjusted movement is enabled. This doesn't affect
            // fullscreen use-cases much, so leave it enabled for GFN.
            // See: https://jirasw.nvidia.com/browse/GSRTC-2500
            return (
                !this.windowedMode && IsChromeVersionAtLeast(this.platformDetails, 84, 0, 4147, 78)
            );
        } else if (IsChromeOS(this.platformDetails) || IsMacOS(this.platformDetails)) {
            return IsChromeVersionAtLeast(this.platformDetails, 86, 0, 4240, 198);
        } else if (IsSafari(this.platformDetails)) {
            // Assume all Safari versions that return a promise from requestPointerLock support unadjustedMovement.
            return true;
        } else {
            return false;
        }
    }

    public getMouseMode(): string {
        // The local and alt here come from Geronimo. "local" meaning local cursor is on and "alt" meaning alternate
        // input is active. "alt" doesn't make too much sense for Ragnarok, but it will make it easier when referring
        // to both Geronimo and Ragnarok at once
        const cursor = this.cursorState.absPositioning ? "local" : "alt";
        const locked = this.isPointerLocked() ? "locked" : "unlocked";
        return `${cursor} ${locked}`;
    }
}

function getRawUpdateOverride(): RawUpdateState | undefined {
    const override = RagnarokSettings.ragnarokConfig.mouseCoalesceInterval;
    if (override !== undefined) {
        const SUPPORTED = new Map([
            [0, RawUpdateState.OFF],
            [4, RawUpdateState.ON_4MS],
            [8, RawUpdateState.ON_8MS],
            [16, RawUpdateState.ON_16MS]
        ]);
        return SUPPORTED.get(override);
    }
    return undefined;
}

function shouldIgnorePointerEvent(evt: PointerEvent): boolean {
    if (!evt.isPrimary) {
        return true;
    }
    // GestureHandler/TouchListener is always available to handle touch/pen events, so ignore those here
    if (evt.pointerType === "touch" || evt.pointerType === "pen") {
        return true;
    }
    return false;
}

function isVisibleCursor(cursor: Cursor): boolean {
    // A cursor is always visible unless it's a system cursor with ID zero, which maps to a style of "none".
    // No other cursor will ever have this style
    return cursor.style !== "none";
}

function getCursorTypeOverride(): CursorType | undefined {
    switch (RagnarokSettings.cursor) {
        case "hw":
            return CursorType.HARDWARE;
        case "sw":
            return CursorType.SOFTWARE;
        case "free":
            return CursorType.FREE;
        default:
            return undefined;
    }
}
